const TILE_CACHE = 'trailblaze-tiles-v2';

const TILE_TEMPLATES = {
  gsi: (z, x, y) => `https://cyberjapandata.gsi.go.jp/xyz/std/${z}/${x}/${y}.png`,
  opentopomap: (z, x, y) => `https://tile.opentopomap.org/${z}/${x}/${y}.png`,
  osm: (z, x, y) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`,
};

export function latLngToTile(lat, lng, zoom) {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lng + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n
  );
  return { x: Math.max(0, Math.min(n - 1, x)), y: Math.max(0, Math.min(n - 1, y)) };
}

export function calculateTileCount(bounds, zoomLevels) {
  let count = 0;
  for (const z of zoomLevels) {
    const topLeft = latLngToTile(bounds.north, bounds.west, z);
    const bottomRight = latLngToTile(bounds.south, bounds.east, z);
    const minX = Math.min(topLeft.x, bottomRight.x);
    const maxX = Math.max(topLeft.x, bottomRight.x);
    const minY = Math.min(topLeft.y, bottomRight.y);
    const maxY = Math.max(topLeft.y, bottomRight.y);
    count += (maxX - minX + 1) * (maxY - minY + 1);
  }
  return count;
}

export async function downloadArea(bounds, tileLayer, zoomLevels, onProgress) {
  const cache = await caches.open(TILE_CACHE);
  const getUrl = TILE_TEMPLATES[tileLayer] || TILE_TEMPLATES.gsi;
  const tiles = [];

  for (const z of zoomLevels) {
    const topLeft = latLngToTile(bounds.north, bounds.west, z);
    const bottomRight = latLngToTile(bounds.south, bounds.east, z);
    const minX = Math.min(topLeft.x, bottomRight.x);
    const maxX = Math.max(topLeft.x, bottomRight.x);
    const minY = Math.min(topLeft.y, bottomRight.y);
    const maxY = Math.max(topLeft.y, bottomRight.y);

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        tiles.push(getUrl(z, x, y));
      }
    }
  }

  // Cap at 2000 tiles
  const cappedTiles = tiles.slice(0, 2000);
  let downloaded = 0;
  let cached = 0;
  let failed = 0;

  // Download in batches of 6 to avoid rate limiting
  const batchSize = 6;
  for (let i = 0; i < cappedTiles.length; i += batchSize) {
    const batch = cappedTiles.slice(i, i + batchSize);
    await Promise.all(
      batch.map(async (url) => {
        try {
          const existing = await cache.match(url);
          if (existing) {
            cached++;
          } else {
            // Both GSI and OpenTopoMap support CORS (Access-Control-Allow-Origin: *)
            const response = await fetch(url);
            if (response.ok) {
              await cache.put(url, response);
              downloaded++;
            } else {
              failed++;
            }
          }
        } catch (e) {
          failed++;
        }
      })
    );
    onProgress(i + batch.length, cappedTiles.length);
  }

  // Get accurate cache count after download
  let actualCacheSize = cappedTiles.length;
  try {
    const keys = await cache.keys();
    actualCacheSize = keys.length;
  } catch {}

  return { total: cappedTiles.length, downloaded, cached, failed, cacheSize: actualCacheSize };
}

export async function getCacheStats() {
  try {
    const cache = await caches.open(TILE_CACHE);
    const keys = await cache.keys();
    const count = keys.length;
    let sizeKB = count * 20;
    try {
      const est = await navigator.storage.estimate();
      if (est.usage) sizeKB = Math.round(est.usage / 1024);
    } catch {}
    return { tileCount: count, estimatedSizeKB: sizeKB };
  } catch (e) {
    return { tileCount: 0, estimatedSizeKB: 0 };
  }
}

export async function clearTileCache() {
  await caches.delete(TILE_CACHE);
}

export function formatCacheSize(kb) {
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
